import { ResolvedMetadataItem } from '../resolvers/default-value/types';
import {
  RefBranch,
  ResolvedSettingTypeMetadata,
  ResolvedStyleTypeMetadata,
  StaticBranch,
} from '../types/metadata/metadataDefinition';
import { DataItemDefaultValue, StyleItemDefaultValue, DataItem } from './types';
import type { CssCustomPropertyItem } from '../types';

type InferDefaultValue<T> = T extends ResolvedStyleTypeMetadata
  ? StyleItemDefaultValue
  : T extends ResolvedSettingTypeMetadata
    ? DataItemDefaultValue
    : T;

export const mapParamToItem = <
  TParamMetadata extends ResolvedMetadataItem,
  TManifestItem extends CssCustomPropertyItem | DataItem,
>({
  metadata,
  params,
  isMobile,
  validate,
  createItem,
}: {
  metadata?: TParamMetadata;
  params: Record<string, { metadata?: TParamMetadata }>;
  isMobile: boolean;
  validate: (type: TParamMetadata['type']) => boolean;
  createItem: (
    type: TParamMetadata['type'],
    defaultValue: InferDefaultValue<TParamMetadata>,
  ) => TManifestItem;
}): TManifestItem | undefined => {
  if (!metadata || !validate(metadata.type)) {
    return undefined;
  }
  const branch = getMetadataValue(metadata, isMobile);
  if (!branch) {
    return undefined;
  }

  switch (branch.type) {
    case 'static':
      return createItem(
        metadata.type,
        getStaticBranchValue(branch as StaticBranch<any>),
      );
    case 'ref':
      return createItem(
        metadata.type,
        getRefBranchValue({
          branch,
          params,
          metadataGetter: (m) => getMetadataValue(m, isMobile),
        }) as InferDefaultValue<TParamMetadata>,
      );
    default:
      const _exhaustiveCheck: never = branch;
      throw new Error(`Unexpected type on: ${_exhaustiveCheck}`);
  }
};

type MetadataValue<Value> =
  | StaticBranch<Value>
  | RefBranch<Value, string, number[]>;

const getMetadataValue = <Value>(
  metadata: ResolvedSettingTypeMetadata | ResolvedStyleTypeMetadata,
  isMobile: boolean,
): MetadataValue<Value> =>
  isMobile
    ? (metadata.mobileDefaultValue ?? metadata.defaultValue)
    : metadata.defaultValue;

export const getStaticBranchValue = <Value>(
  branch: StaticBranch<Value>,
): Value => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { type: _type, ...rest } = branch;
  const isPrimitiveValue = Object.keys(rest).length === 1 && 'value' in rest;
  return isPrimitiveValue ? (rest.value as Value) : (rest as Value);
};

/*
  getRefBranchValue will go through the ref chain to extract the value, if its not possible or value is undefined,
  fallback will be used to resolve the value
*/
export const getRefBranchValue = <Value, D extends number[]>({
  branch,
  params,
  metadataGetter,
}: {
  branch: RefBranch<Value, string, D>;
  params: Record<string, { metadata?: ResolvedMetadataItem }>;
  metadataGetter: <M extends ResolvedMetadataItem>(
    metadata: M,
  ) => MetadataValue<Value>;
}): Value | undefined => {
  const primaryBranch = resolveRefChain(branch, params, metadataGetter);
  if (primaryBranch) {
    return primaryBranch;
  }

  if (branch.fallback) {
    switch (branch.fallback.type) {
      case 'static':
        return getStaticBranchValue(branch.fallback as StaticBranch<any>);
      case 'ref':
        return resolveRefChain(branch.fallback, params, metadataGetter);
      default:
        const _exhaustiveCheck: never = branch.fallback;
        throw new Error(`Unexpected type on: ${_exhaustiveCheck}`);
    }
  }

  return undefined;
};

const MAX_DEPTH = 5;

const resolveRefChain = <Value, D extends number[]>(
  branch: RefBranch<Value, string, D>,
  params: Record<string, { metadata?: ResolvedMetadataItem }>,
  metadataGetter: <M extends ResolvedMetadataItem>(
    m: M,
  ) => MetadataValue<Value>,
  visited = new Set<string>(),
  depth = 0,
): Value | undefined => {
  if (depth >= MAX_DEPTH) {
    console.error(`Max depth reached at ${branch.ref}`);
    return undefined;
  }
  if (visited.has(branch.ref)) {
    console.error(`Circular reference at ${branch.ref}`);
    return undefined;
  }
  visited.add(branch.ref);

  const metadata = params[branch.ref]?.metadata;

  const next = metadata && metadataGetter(metadata);
  if (!next) {
    return undefined;
  }

  switch (next.type) {
    case 'static':
      return getStaticBranchValue(next);
    case 'ref':
      return resolveRefChain(next, params, metadataGetter, visited, depth + 1);
    default:
      const _exhaustiveCheck: never = next;
      throw new Error(`Unexpected type on: ${_exhaustiveCheck}`);
  }
};
